// 빛기둥 — 굴 천장 빛샘(수직 갱)으로 떨어지는 햇살 한 줄기.
//
// [왜 메시인가] 후처리 GodRays 는 지하에서 성립하지 않는다 — 그 기법은 «화면에 잡힌 태양
//   픽셀»에서 방사로 번지게 하는 것이라, 태양이 화면 밖인 굴 안에서는 그릴 근거가 없다.
//   그래서 빛을 물체로 짓는다: 가산 합성(additive) 반투명 원뿔 하나가 곧 공중 산란이다.
//
// [자리는 어디서 오나] `front/public/data/cave_lightshafts.json` — `carve_hall_lightshafts.py`
//   가 갱을 뚫는 «그 회차에» 같이 쓴다. 좌표를 여기 적지 마라. 갱을 옮기면 빛만 제자리에
//   남고, 그 어긋남은 굴에 들어가 봐야 보인다.
//
// [언제 그리나] 카메라가 굴 안이고(`surfaceHidden`) 반경 안일 때만. 지상에서는 원뿔이
//   땅을 뚫고 솟은 흰 고깔이 된다.
// [밝기] 낮에만 든다 — 햇빛이 원인이므로 밤에는 0 이다(`SKY_NIGHT`).
import { useEffect, useMemo, useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { surfaceHidden } from './caveLight';
import { SKY_NIGHT, SKY_SUN_COLOR } from './sky/skyLight';

const DRAW_R = 260;          // m — 이 밖의 빛기둥은 안 그린다
const RADIAL = 24;           // 원뿔 둘레 분할 — 24 면 10m 반경에서 각이 안 보인다

// 가산 합성이라 «겹칠수록 밝다». 원뿔 한 개가 안팎 두 면이므로 세기는 그 값의 절반으로 잡는다.
const STRENGTH = 0.5;

const VERT = `
  varying float vT;          // 0 = 지표 구멍, 1 = 바닥
  varying vec3 vWorld;
  varying vec3 vNormalW;
  void main() {
    // CylinderGeometry 의 y 는 +0.5(위) ~ -0.5(아래) 다.
    vT = 0.5 - position.y;
    vec4 w = modelMatrix * vec4(position, 1.0);
    vWorld = w.xyz;
    vNormalW = normalize(mat3(modelMatrix) * normal);
    gl_Position = projectionMatrix * viewMatrix * w;
  }
`;

// 가장자리에서 투명해져야 «기둥»이지 «고깔»이 아니다. 시선과 면이 스칠수록(연직 입사가
// 아닐수록) 빛이 두껍게 쌓이므로, 그리즘 두께를 법선·시선 내적으로 근사한다.
// 결 — 둘레 방향 줄무늬가 천천히 아래로 흐른다(빛이 «떨어지는 중»으로 읽힌다). 평균 밝기는 그대로.
const FRAG = `
  uniform vec3 uColor;
  uniform float uAmt;        // 0 이면 한 픽셀도 안 그린다
  uniform float uTime;
  varying float vT;
  varying vec3 vWorld;
  varying vec3 vNormalW;
  void main() {
    if (uAmt <= 0.001) discard;
    vec3 v = normalize(cameraPosition - vWorld);
    // 면을 «스쳐» 볼수록 두껍다 — 정면으로 보는 면은 얇다.
    float graze = 1.0 - abs(dot(v, vNormalW));
    float thick = pow(clamp(graze, 0.0, 1.0), 1.6);
    // 아래로 갈수록 옅어진다. 공기가 빛을 먹어서이기도 하고, 바닥에서 뚝 끊기면
    // 원뿔의 «테두리 원»이 보여 물체로 읽히기 때문이다.
    float down = pow(1.0 - clamp(vT, 0.0, 1.0), 1.5);
    float ang = atan(vNormalW.z, vNormalW.x);
    float streak = 0.55 * sin(ang * 9.0 + vT * 3.0 - uTime * 0.35)
                 + 0.45 * sin(ang * 23.0 - vT * 5.0 + uTime * 0.21);
    float a = thick * down * uAmt * (1.0 + 0.35 * streak);
    gl_FragColor = vec4(uColor * a, a);
  }
`;

/** 원뿔 하나 — a(지표 구멍) 에서 b(바닥) 로 선다. */
function Shaft({ shaft }) {
  const ref = useRef(null);
  const mat = useRef(null);

  const geo = useMemo(() => {
    const [ax, ay, az] = shaft.a;
    const [bx, by, bz] = shaft.b;
    const len = Math.hypot(bx - ax, by - ay, bz - az);
    // openEnded — 뚜껑이 있으면 위아래에서 원반 두 장이 보인다.
    return new THREE.CylinderGeometry(shaft.rTop, shaft.rBot, len, RADIAL, 1, true);
  }, [shaft]);

  const { pos, quat } = useMemo(() => {
    const a = new THREE.Vector3(...shaft.a);
    const b = new THREE.Vector3(...shaft.b);
    const mid = a.clone().add(b).multiplyScalar(0.5);
    const dir = b.clone().sub(a).normalize();
    // 실린더의 기본 축은 +Y 이고 우리 축은 a→b(아래 방향)라 -Y 에 맞춘다.
    const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, -1, 0), dir);
    return { pos: mid, quat: q };
  }, [shaft]);

  useEffect(() => () => geo.dispose(), [geo]);

  useFrame(({ camera }, dt) => {
    const m = mat.current;
    const o = ref.current;
    if (!m || !o) return;
    m.uniforms.uTime.value += Math.min(dt, 0.1);
    const d = camera.position.distanceTo(pos);
    // 「굴 안인가」는 caveLight 한 곳에서만 묻는다 — 정의를 두 벌 두면 갈린다.
    const inside = surfaceHidden() && d < DRAW_R;
    const day = 1 - Math.min(1, Math.max(0, SKY_NIGHT.value));
    // 멀수록 옅게 — 굴 복도 끝에서 벽 너머로 새어 보이는 것을 줄인다.
    const near = 1 - Math.min(1, d / DRAW_R);
    const amt = inside ? STRENGTH * day * (0.35 + 0.65 * near) : 0;
    m.uniforms.uAmt.value = amt;
    m.uniforms.uColor.value.copy(SKY_SUN_COLOR.value);
    o.visible = amt > 0.001;
  });

  return (
    <mesh ref={ref} geometry={geo} position={pos} quaternion={quat}
          frustumCulled renderOrder={9} visible={false}>
      <shaderMaterial
        ref={mat}
        vertexShader={VERT}
        fragmentShader={FRAG}
        uniforms={{ uColor: { value: new THREE.Color(1, 1, 1) }, uAmt: { value: 0 }, uTime: { value: 0 } }}
        transparent
        depthWrite={false}
        blending={THREE.AdditiveBlending}
        side={THREE.DoubleSide}
        toneMapped={false}
      />
    </mesh>
  );
}

export default function CaveLightShafts() {
  const [shafts, setShafts] = useState(null);

  useEffect(() => {
    let alive = true;
    fetch('/data/cave_lightshafts.json')
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (alive && j?.shafts?.length) setShafts(j.shafts); })
      .catch(() => {});      // 자리표가 없으면 빛기둥이 없을 뿐이다 — 월드는 선다
    return () => { alive = false; };
  }, []);

  if (!shafts) return null;
  return shafts.map((s, i) => <Shaft key={`${s.label}-${i}`} shaft={s} />);
}
